import { useCallback, useEffect, useRef, useState } from "react";
import { authHeader, getUserFacingErrorMessage, handleResponse, logErrorForDev } from "../../utils";
import { IRequestValues, IPagedViewResponse, SortOrder } from "../../components/UI/Declarations";

interface UsePagedListDataProps {
    getUrl: string;
    pageSize: number;
    defaultSortBy: string;
    defaultSortOrder: SortOrder;
    refreshNumber?: number;
    items?: IPagedViewResponse;
}

export const usePagedListData = ({
    getUrl,
    pageSize,
    defaultSortBy,
    defaultSortOrder,
    refreshNumber,
    items
}: UsePagedListDataProps) => {
    const { offset: itemsOffset = 0, count: itemsCount = 0, total: itemsTotal = 0, results: itemsResults } = items || {};
    
    const [results, setResults] = useState(itemsResults);
    const [currentPage, setCurrentPage] = useState(1);
    const [offset, setOffset] = useState(itemsOffset);
    const [first, setFirst] = useState(itemsOffset + 1);
    const [last, setLast] = useState(Math.min(offset + itemsCount, itemsTotal));
    const [lastPage, setLastPage] = useState(1);
    const [sortBy, setSortBy] = useState(defaultSortBy);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [isBusy, setIsBusy] = useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>();
    /** Row count across all pages from the API - not `results.length` on the current page. */
    const [total, setTotal] = useState(itemsTotal);

    const hasResults = results !== undefined;

    const requestIdRef = useRef(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!items) return;
        const t = items.total ?? 0;
        setResults(items.results);
        setOffset(items.offset);
        setCurrentPage(Math.floor(items.offset / pageSize) + 1);
        setFirst(items.offset + 1);
        setLast(Math.min(items.offset + items.count, t));
        setLastPage(Math.ceil(t / pageSize) || 1);
        setTotal(t);
    }, [items, pageSize]);

    const getResults = useCallback((requestValues: IRequestValues) => {
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const requestId = ++requestIdRef.current;
        setIsBusy(true);
        setHasLoadError(false);
        setLoadErrorMessage(null);

        const requestOptions = {
            method: "POST",
            headers: authHeader(),
            body: JSON.stringify(requestValues),
            signal: abortController.signal,
        };

        fetch(getUrl, requestOptions)
            .then(handleResponse)
            .then((response: IPagedViewResponse) => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                const responseTotal = response.total ?? 0;
                setResults(response.results);
                setOffset(response.offset);
                setFirst(response.offset + 1);
                setLast(Math.min(response.offset + response.count, responseTotal));
                setLastPage(Math.ceil(responseTotal / pageSize) || 1);
                setTotal(responseTotal);
                setIsBusy(false);
            })
            .catch((error: unknown) => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                if (error instanceof DOMException && error.name === "AbortError") {
                    return;
                }
                logErrorForDev("usePagedListData", error);
                setHasLoadError(true);
                setLoadErrorMessage(
                    getUserFacingErrorMessage(error, "An error occurred while loading data")
                );
                setIsBusy(false);
            });
    }, [getUrl, pageSize]);

    const buildPages = useCallback((offset: number, total: number) => {
        const pageCount = Math.ceil(total / pageSize);
        const currentPageNumber = Math.floor(offset / pageSize) + 1;
        const pages: number[] = [];

        if (pageCount <= 7) {
            for (let i = 1; i <= pageCount; i++) {
                pages.push(i);
            }
        } else {
            if (currentPageNumber <= 4) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push(-1);
                pages.push(pageCount);
            } else if (currentPageNumber >= pageCount - 3) {
                pages.push(1);
                pages.push(-1);
                for (let i = pageCount - 4; i <= pageCount; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push(-1);
                for (let i = currentPageNumber - 1; i <= currentPageNumber + 1; i++) {
                    pages.push(i);
                }
                pages.push(-1);
                pages.push(pageCount);
            }
        }

        return pages;
    }, [pageSize]);

    const handleSortByClicked = useCallback((member: string) => {
        if (sortBy === member) {
            setSortOrder(sortOrder === SortOrder.ascending ? SortOrder.descending : SortOrder.ascending);
        } else {
            setSortBy(member);
            setSortOrder(SortOrder.ascending);
        }
    }, [sortBy, sortOrder]);

    const setCurrentPageAndUpdate = useCallback((page: number) => {
        setCurrentPage(page);
        const newOffset = (page - 1) * pageSize;
        setOffset(newOffset);
    }, [pageSize]);

    return {
        results,
        setResults,
        currentPage,
        setCurrentPage: setCurrentPageAndUpdate,
        offset,
        setOffset,
        first,
        setFirst,
        last,
        setLast,
        lastPage,
        setLastPage,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        isBusy,
        setIsBusy,
        hasLoadError,
        setHasLoadError,
        loadErrorMessage,
        setLoadErrorMessage,
        total,
        hasResults,
        getResults,
        buildPages,
        handleSortByClicked
    };
};
